import { Boundary } from '../../ui/boundary';
import type { ParamSpec } from '@flobi/artwork-core/plugin';
import { useState } from 'react';
import { Icon } from '../chrome/icons';
import { Button, Hint, IconButton, NumberField, PopUpButton, SegmentedControl, Slider, Switch } from '../../ui/controls';
import { ConfirmAlert } from '../../ui/confirm';
import type { AddonSummary } from '../addons/api';
import { EXAMPLES } from '../addons/examples';
import { useAddonsContext } from '../addons/useAddons';
import { Section } from './parts';

/**
 * Add-ons, for real.
 *
 * Two views in one drawer: the list of what this person can use, and one add-on opened
 * — its question, asked in Artwork's own controls, and the one thing it does. The form is
 * declared by the add-on and drawn here, the same bargain a tool's rail makes: the add-on
 * says what it needs, the editor decides how that looks.
 */
export function AddonsPanel() {
  const addons = useAddonsContext();
  // Keyed by the add-on, so opening another one — Make a Copy opens the copy — starts its
  // form afresh. Kept mounted, the Values box went on showing the text typed into the
  // original while the copy ran on its defaults (QA 2026-09-24, library-addons-F12).
  return (
    <Boundary message="This add-on could not be shown. It may be declaring its options wrongly.">
      {addons.open ? <Opened key={addons.open.id} /> : <Listing />}
    </Boundary>
  );
}

/**
 * Add from Code's draft, kept outside the component: closing the Library, switching its
 * view or pressing ⌃⌘S unmounted the box and threw the typed code away
 * (QA 2026-09-24, library-addons-F2).
 */
const kept = { adding: false, source: '' };

function Listing() {
  const addons = useAddonsContext();
  const [adding, setAddingState] = useState(kept.adding);
  const setAdding = (value: boolean) => {
    kept.adding = value;
    setAddingState(value);
  };
  const items = addons.items ?? [];
  const mine = items.filter((item) => item.mine);
  const shared = items.filter((item) => !item.mine && item.visibility === 'workspace');
  const library = items.filter((item) => item.visibility === 'library');

  return (
    <>
      <Notice />
      {addons.problem && <p className="mb-[12px] text-[12px] text-label-2">{addons.problem}</p>}

      {adding ? (
        <AddFromCode onDone={() => setAdding(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mb-[16px] flex w-full items-center gap-[10px] rounded-[12px] bg-fill-2 px-[10px] py-[9px] text-left transition-colors hover:bg-fill-selected"
        >
          <span className="grid size-[32px] shrink-0 place-items-center rounded-[9px] bg-accent text-on-accent">
            <Icon name="plus" size={16} />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] font-medium text-label">Add from Code</span>
            <span className="block text-[11px] text-label-2">Paste an add-on, or start from an example</span>
          </span>
        </button>
      )}

      {addons.items === null && !addons.problem ? (
        <p className="text-[12px] text-label-2">Loading add-ons…</p>
      ) : items.length === 0 ? null : (
        <>
          {mine.length > 0 && <Group title="Yours" items={mine} />}
          {shared.length > 0 && <Group title="From your workspace" items={shared} />}
          {library.length > 0 && <Group title="Flobi library" items={library} />}
        </>
      )}
    </>
  );
}

function Group({ title, items }: { title: string; items: readonly AddonSummary[] }) {
  const addons = useAddonsContext();
  return (
    <Section title={title} all={false}>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-[2px]">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => void addons.show(item.id)}
            className="flex w-full items-center gap-[10px] rounded-[10px] px-[6px] py-[6px] text-left transition-colors hover:bg-fill-2"
          >
            <span className="grid size-[32px] shrink-0 place-items-center rounded-[9px] bg-fill-1 text-label-2">
              <Icon name={item.kind === 'tool' ? 'cursor' : 'addons'} size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] text-label">{item.name}</span>
              <span className="block truncate text-[11px] text-label-2">
                {item.kind === 'tool' ? 'Tool' : 'Command'}
                {item.description ? ` · ${item.description}` : ''}
              </span>
            </span>
            {addons.holding === item.id && <span className="shrink-0 rounded-full bg-accent-soft px-[8px] py-[2px] text-[11px] font-medium text-accent-text">In Hand</span>}
          </button>
        ))}
      </div>
    </Section>
  );
}

function AddFromCode({ onDone }: { onDone: () => void }) {
  const addons = useAddonsContext();
  const [source, setSourceState] = useState(kept.source);
  const setSource = (value: string) => {
    kept.source = value;
    setSourceState(value);
  };
  return (
    <div className="mb-[16px] grid gap-[8px]">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-label">Add from Code</span>
        <Button size="small" variant="borderless" onClick={onDone}>
          Cancel
        </Button>
      </div>
      <div className="flex flex-wrap gap-[6px]">
        {EXAMPLES.map((example) => (
          <Button key={example.name} size="small" onClick={() => setSource(example.source)}>
            {example.name}
          </Button>
        ))}
      </div>
      <label htmlFor="addon-source" className="sr-only">
        Add-on code
      </label>
      <textarea
        id="addon-source"
        value={source}
        onChange={(event) => setSource(event.target.value)}
        // Escape in the code is the code's, not the drawer's.
        onKeyDown={(event) => {
          if (event.key === 'Escape') event.stopPropagation();
        }}
        spellCheck={false}
        placeholder="definePlugin({ name: '…', run: function (art, p) { … } });"
        className="ui-scroll h-48 w-full resize-y rounded-[10px] bg-fill-1 p-[10px] font-mono text-[11px] leading-[1.5] text-label outline-none "
      />
      <Hint>Plain JavaScript for now. It runs in a sandbox with no network and no access to anything but this file.</Hint>
      <Button
        variant="prominent"
        full
        disabled={addons.busy || source.trim() === ''}
        onClick={async () => {
          if (await addons.addFromCode(source)) {
            kept.source = '';
            onDone();
          }
        }}
      >
        {addons.busy ? 'Checking…' : 'Add'}
      </Button>
    </div>
  );
}

function Opened() {
  const addons = useAddonsContext();
  const open = addons.open!;
  const manifest = open.version.manifest;
  // In the order the author declared them. The stored manifest is jsonb, which re-sorts
  // object keys, so the order has to come from `paramOrder` rather than from the object.
  const declared = manifest.paramOrder ?? [];
  const rank = (name: string) => (declared.includes(name) ? declared.indexOf(name) : declared.length);
  const specs = (Object.entries(manifest.params ?? {}) as [string, ParamSpec][]).sort((a, b) => rank(a[0]) - rank(b[0]));
  const isTool = open.kind === 'tool';
  const inHand = addons.holding === open.id;
  const mine = addons.items?.find((item) => item.id === open.id)?.mine ?? false;
  const [removing, setRemoving] = useState(false);

  return (
    <>
      <Button size="small" variant="borderless" icon="back" onClick={() => void addons.show(null)} className="mb-[10px] -ml-[8px]">
        All Add-ons
      </Button>
      <h3 className="text-[15px] font-semibold text-label">{open.name}</h3>
      <p className="mb-[14px] mt-[2px] text-[11px] text-label-2">
        {isTool ? 'Tool' : 'Command'} · version {open.version.hash.slice(0, 7)}
        {manifest.description ? ` · ${manifest.description}` : ''}
      </p>

      {specs.length > 0 && (
        <div className="mb-[16px] grid gap-[12px]">
          {specs.map(([name, spec]) => (
            <Field key={name} name={name} spec={spec} value={addons.params[name]} onChange={(value) => addons.setParam(name, value)} />
          ))}
        </div>
      )}

      <Button variant="prominent" size="large" full disabled={addons.busy} aria-busy={addons.busy} onClick={() => void (isTool ? addons.hold() : addons.run())}>
        {isTool ? 'Pick Up the Tool' : 'Run'}
      </Button>
      {isTool && inHand && <p className="mt-[8px] text-[11px] text-label-2">In hand. Draw on the page; its options are above the tools.</p>}

      <Notice />

      {!mine && (
        <div className="mt-[20px] grid gap-[6px] border-t border-separator pt-[12px]">
          <Button size="small" disabled={addons.busy} onClick={() => void addons.copy()} className="justify-self-start">
            Make a Copy to Change It
          </Button>
          <p className="text-[11px] text-label-2">
            {open.visibility === 'library'
              ? 'This one is Flobi’s. A copy is yours, private, and changes nothing for anybody else.'
              : 'This one belongs to somebody in your workspace. A copy is yours and private.'}
          </p>
        </div>
      )}

      {mine && (
        <div className="mt-[20px] grid gap-[10px] border-t border-separator pt-[12px]">
          <SegmentedControl
            label="Shared with"
            value={open.visibility === 'workspace' ? 'workspace' : 'private'}
            onChange={(choice) => void addons.share(choice)}
            segments={[
              { value: 'private', label: 'Only Me' },
              { value: 'workspace', label: 'Workspace' },
            ]}
          />
          <Button size="small" variant="destructive" disabled={addons.busy} onClick={() => setRemoving(true)} className="justify-self-start">
            Remove This Add-on
          </Button>
        </div>
      )}

      {/* Asked first: one press used to delete it outright, with nothing in the app to
          bring it back (QA 2026-09-24, library-addons-F11). */}
      {removing ? (
        <ConfirmAlert
          title={`Remove “${open.name}”?`}
          message="It can’t be brought back, for you or anyone it is shared with."
          action="Remove"
          onCancel={() => setRemoving(false)}
          onConfirm={() => {
            setRemoving(false);
            void addons.remove();
          }}
        />
      ) : null}
    </>
  );
}

function Notice() {
  const addons = useAddonsContext();
  if (!addons.notice) return null;
  return (
    <div role="status" className="mt-[12px] flex items-start gap-[8px] rounded-[10px] bg-fill-2 py-[8px] pl-[10px] pr-[4px]">
      <p className="min-w-0 flex-1 pt-[2px] text-[12px] text-label">{addons.notice}</p>
      <IconButton icon="close" label="Dismiss" size={22} onClick={addons.dismiss} />
    </div>
  );
}

/** One param, as the control that kind of answer wants. */
function Field({ name, spec, value, onChange }: { name: string; spec: ParamSpec; value: unknown; onChange: (value: unknown) => void }) {
  const id = `addon-param-${name}`;
  const input = 'w-full rounded-[8px] bg-fill-1 px-[9px] py-[6px] text-[13px] text-label outline-none ';
  const label = (
    <label htmlFor={id} className="mb-[4px] block text-[12px] text-label-2">
      {spec.label}
    </label>
  );
  const help = spec.help ? <p className="mt-[4px] text-[11px] text-label-2">{spec.help}</p> : null;

  switch (spec.type) {
    case 'number': {
      const current = typeof value === 'number' ? value : spec.default;
      return (
        <div>
          <div className="flex items-center gap-[8px]">
            <span className="min-w-0 flex-1 text-[12px] text-label-2">{spec.label}</span>
            <NumberField className="w-[76px]" label={spec.label} value={current} min={spec.min} max={spec.max} step={spec.step ?? 1} decimals={2} onChange={(next) => onChange(next)} />
          </div>
          {spec.min !== undefined && spec.max !== undefined ? (
            <Slider label={spec.label} value={current} min={spec.min} max={spec.max} step={spec.step ?? (spec.max - spec.min > 20 ? 1 : 0.01)} onChange={(next) => onChange(next)} />
          ) : null}
          {help}
        </div>
      );
    }
    case 'string':
      return (
        <div>
          {label}
          {spec.multiline ? (
            <textarea id={id} value={String(value ?? spec.default)} onChange={(e) => onChange(e.target.value)} className={`${input} h-20`} />
          ) : (
            <input id={id} type="text" value={String(value ?? spec.default)} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.stopPropagation()} className={input} />
          )}
          {help}
        </div>
      );
    case 'strings':
    case 'numbers': {
      const list = Array.isArray(value) ? value : [...spec.default];
      return (
        <div>
          {label}
          <input
            id={id}
            type="text"
            defaultValue={list.join(', ')}
            onKeyDown={(e) => e.stopPropagation()}
            onChange={(event) => {
              const parts = event.target.value.split(',').map((part) => part.trim()).filter(Boolean);
              onChange(spec.type === 'numbers' ? parts.map(Number).filter(Number.isFinite) : parts);
            }}
            className={`${input} tnum`}
          />
          <p className="mt-[4px] text-[11px] text-label-2">{help ? spec.help : 'Separate them with commas.'}</p>
        </div>
      );
    }
    case 'colour':
      return (
        <div>
          {label}
          <div className="flex items-center gap-[8px]">
            <input id={id} type="color" value={String(value ?? spec.default).slice(0, 7)} onChange={(e) => onChange(e.target.value)} className="h-[26px] w-[38px] cursor-default rounded-[7px] bg-transparent" />
            <span className="tnum text-[12px] uppercase text-label-2">{String(value ?? spec.default)}</span>
          </div>
          {help}
        </div>
      );
    case 'boolean':
      return (
        <div className="flex items-center gap-[8px]">
          <span className="min-w-0 flex-1 text-[13px] text-label">{spec.label}</span>
          <Switch label={spec.label} checked={value === undefined ? spec.default : value === true} onChange={(on) => onChange(on)} />
        </div>
      );
    case 'choice': {
      if (spec.many) {
        const chosen = Array.isArray(value) ? (value as string[]) : [...spec.default];
        return (
          <fieldset>
            <legend className="mb-[4px] text-[12px] text-label-2">{spec.label}</legend>
            <div className="flex flex-wrap gap-[6px]">
              {spec.options.map((option) => {
                const on = chosen.includes(option);
                return (
                  <Button key={option} size="small" variant={on ? 'prominent' : 'plain'} aria-pressed={on} onClick={() => onChange(on ? chosen.filter((c) => c !== option) : [...chosen, option])}>
                    {option}
                  </Button>
                );
              })}
            </div>
            {help}
          </fieldset>
        );
      }
      return (
        <div>
          {label}
          <PopUpButton label={spec.label} value={String(value ?? spec.default)} onChange={(next) => onChange(next)} options={spec.options.map((option) => [option, option] as const)} />
          {help}
        </div>
      );
    }
  }
}
